﻿import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { NonIdealState, Icon } from "@blueprintjs/core";
import { ListingPagination } from 'components/listing';
import { FormSelect } from 'components/form-fields';
import { ShowHide } from 'components/layout/ShowHide';
import 'components/listing/Listing.css';
import { Table } from 'components/listing/Table';
import classNames from "classnames";

const rows25 = 25;
const rows50 = 50;
const rows100 = 100;

const perPageOptions = [rows25, rows50, rows100];

export function ListingTable(
    {
        id = null,
        loadingData = true,
        data = [],
        headers = [],
        totalRecordCount = 0,
        sortable = false,
        sortableHeaders = null,
        sortedBy = '',
        sortedDir = 'A',
        onSort = null,
        pageable = true,
        showPerPage = true,
        startPage = null,
        shrinkLastColumn = false,
        noWrap = false,
        allowTableScroll = false,
        allowTableScrollVertical = false,
        columnClasses = [],
        loadingClass = "bp6-skeleton",
        pageSize = 25,
        columnWidths = [],
        rowClasses = [],
        rowClickEnabled = false,
        fitToContent = false,
        fixedHeight = false,
        tableClasses = "",
        noDataHeading,
        noDataMessage,
        onPagingChange,
        onRowClick
    }
) {

    const [perPage, setPerPage] = useState(perPageOptions[perPageOptions.indexOf(pageSize)]);
    const [currentPage, setCurrentPage] = useState(startPage ?? 1);
    const [totalPages, setTotalPages] = useState(1);
    const dummyLoadData = [];
    for (let i = 0; i < perPage; i++) {

        let cols = []

        for (let j = 0; j < headers.length; j++) {
            const maxLength = 36;
            cols.push(Math.random().toString(maxLength).replace(/[^a-z]+/g, ''));
        }

        dummyLoadData.push({
            key: i,
            cells: cols
        });
    }

    useEffect(() => {
        if (startPage) {
            setCurrentPage(startPage);
        }
    }, [startPage]);

    useEffect(() => {
        if (pageSize) {
            let rowsPerPage = perPageOptions[perPageOptions.indexOf(pageSize)];
            if (rowsPerPage && rowsPerPage !== perPage) {
                setPerPage(rowsPerPage);
            }
        }
    }, [pageSize]);

    useEffect(() => {
        let pageCount = Math.max(1, Math.ceil(totalRecordCount / perPage));
        setTotalPages(pageCount);
        setCurrentPage(startPage ?? 1);

    }, [perPage, totalRecordCount]);

    function onPageSizeChange(item) {
        if (item.id !== perPage) {
            setPerPage(item.id);
            setCurrentPage(1);
            onPagingChange(1, item.id);
        }
    }

    function onPaginationChange(page) {
        if (page !== currentPage) {
            setCurrentPage(page);
            onPagingChange(page, perPage);
        }
    }

    function getHeaderName(headerName) {
        if (typeof headerName === 'string') {
            return headerName;
        }
        else 
        {
            return headerName.props.content;
        }
    }

    function renderTableHeaderCell(headerName) {
        let matchedSort = false;
        let matchedMapping;

        //The sortableHeaders can either be list of string or list of {headerName: sortName:} objects
        //The 'headerName' can either be a string or a div element
        if (sortable && sortableHeaders?.length > 0) {
            if (typeof sortableHeaders[0] === 'string') {
                if (typeof headerName === 'string') {
                    matchedSort = sortableHeaders.includes(headerName);
                }
                else 
                {
                    matchedSort = sortableHeaders.includes(headerName.key);
                }
            }
            else {
                matchedMapping = sortableHeaders.find(
                    function (sortHead) {
                        if (typeof headerName === 'string') {
                            return sortHead.headerName === headerName;
                        }
                        else 
                        {
                            return sortHead.headerName === headerName.key;
                        }
                    }
                );
            }
        }

        if (!matchedSort && !matchedMapping) {
            return headerName;
        } else {
            var icon = null;

            if ((matchedSort && getHeaderName(headerName) === sortedBy) ||
                (matchedMapping && matchedMapping?.headerName === sortedBy?.headerName)) {
                var iconName = sortedDir === 'A' ? "caret-down" : "caret-up";
                icon = (<Icon icon={iconName} />);
            }

            return (<span className="list-table-clickable-header" onClick={() =>
                onTableHeaderSort(matchedMapping ? matchedMapping : headerName)}
                onKeyDown={(e) => { }}>
                {matchedMapping ? matchedMapping.headerName : headerName} {icon}
            </span>);
        }
    }

    function onTableHeaderSort(header) {
        let sortDir;
        if (typeof header === 'string') {
            sortDir = sortedDir === 'A' && sortedBy === header ? 'D' : 'A';
        }
        else {
            sortDir = sortedDir === 'A' && sortedBy.headerName === header.headerName ? 'D' : 'A';
        }

        if (onSort != null) {
            onSort(header, sortDir);
        }
    }

    function formatHeaders() {
        return {
            headers: headers.map((value) => {
                let thisKey = value;
                if (!(typeof value === 'string')) {
                    thisKey = value.key;
                }
                return {
                    key: thisKey,
                    value: renderTableHeaderCell(value)
                }
            })
        };
    }

    function getRowClass(rowIndex) {
        if (!rowClasses) {
            return null;
        }
        if (rowClasses.length > rowIndex -1) {
            return rowClasses[rowIndex];
        }
        return null;
    }

    function formatData() {
        return data.map((row, rowIndex) => {
            return {
                cells: row,
                key: rowIndex,
                onRowClick: rowClickEnabled ? () => onRowClick(rowIndex) : null,
                class: getRowClass(rowIndex)
            };
        });
    }

    return (
        <div id={id} className={classNames({"fixed-height-table": fixedHeight})}>
            <ShowHide
                evaluator={totalRecordCount === 0 && !loadingData}
                show={(
                    <Fragment>
                        <div className="clear-divide"></div>
                        <div className="list-table-no-results">
                            <NonIdealState
                                title={noDataHeading || "Nothing to see here..."}
                                description={noDataMessage}
                            />
                        </div>
                    </Fragment>

                )}
                hide={(

                    <Fragment>
                        <div className={classNames(allowTableScroll ? "listing-table-scroll" : "", allowTableScrollVertical ? "listing-table-scroll-vertical" : "")}>
                            <Table
                                    data={totalRecordCount === 0 && loadingData ? dummyLoadData : formatData()}
                                    headerData={formatHeaders()}
                                    loadingData={loadingData}
                                    noWrap={noWrap}
                                    shrinkLastColumn={shrinkLastColumn}
                                    columnClasses={columnClasses}
                                    columnWidths={columnWidths}
                                    loadingClass={loadingClass}
                                    fitToContent={fitToContent}
                                    tableClasses={tableClasses}
                            />
                        </div>
                        <div className='listing-table-controls'>
                            <div>
                                <ShowHide
                                    evaluator={pageable}
                                    show={(
                                        <ListingPagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPaginationChange} loadingData={loadingData}></ListingPagination>
                                    )}
                                ></ShowHide>
                            </div>
                            <div>
                                <ShowHide
                                    evaluator={pageable && showPerPage}
                                    show={(
                                        <Fragment>
                                            <div className="listing-table-filter-fields listing-table-per-page">
                                                Show
                                                <FormSelect
                                                    items={perPageOptions.map(m => { return { id: m, name: m + " rows" } })}
                                                    placeholder="Items"
                                                    onItemSelect={onPageSizeChange}
                                                    selectedValue={perPage}
                                                    disabled={loadingData}
                                                    dynamicWidth={true}
                                                ></FormSelect>
                                                per page
                                            </div>
                                        </Fragment>
                                    )}
                                ></ShowHide>
                            </div>
                        </div>
                    </Fragment>

                )}
            ></ShowHide>
        </div>
    );
}

ListingTable.propTypes = {
    id: PropTypes.string,
    loadingData: PropTypes.bool.isRequired,
    data: PropTypes.array,
    headers: PropTypes.array,
    totalRecordCount: PropTypes.number,
    onPagingChange: PropTypes.func,
    noDataMessage: PropTypes.string,
    noDataHeading: PropTypes.string,
    sortable: PropTypes.bool,
    sortableHeaders: PropTypes.array,
    sortedBy: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]),
    sortedDir: PropTypes.string,
    onSort: PropTypes.func,
    pageable: PropTypes.bool,
    showPerPage: PropTypes.bool,
    startPage: PropTypes.number,
    shrinkLastColumn: PropTypes.bool,
    noWrap: PropTypes.bool,
    allowTableScroll : PropTypes.bool,
    allowTableScrollVertical: PropTypes.bool,
    columnClasses: PropTypes.array,
    loadingClass : PropTypes.string,
    pageSize: PropTypes.oneOf(perPageOptions),
    columnWidths: PropTypes.array,
    rowClasses: PropTypes.array,
    rowClickEnabled: PropTypes.bool,
    onRowClick: PropTypes.func,
    fitToContent: PropTypes.bool,
    fixedHeight: PropTypes.bool,
    tableClasses: PropTypes.string
};
